import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { getComerciosService } from "modules/jarvis/services/commerceService";
import { commerceStats, filterCommerces } from "../model/commerceModel";
const initialFilters = { tipo: "todos", activo: "todos", busqueda: "" };

// Solo la última consulta puede actualizar el catálogo y su fecha de carga.
export const useComerciosJarvis = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtros, setFiltros] = useState(initialFilters);
  const mounted = useRef(false),
    revision = useRef(0),
    pending = useRef(null);
  const loadComercios = useCallback(async () => {
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    const request = ++revision.current;
    setLoading(true);
    setError(null);
    try {
      const result = await getComerciosService({ signal: controller.signal });
      if (!mounted.current || request !== revision.current || result.cancelled) return null;
      if (result.success) {
        setRows(result.data);
        setLastUpdated(new Date().toISOString());
      } else setError(result.error);
      return result;
    } catch (err) {
      if (!mounted.current || request !== revision.current) return null;
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      if (mounted.current && request === revision.current) {
        setLoading(false);
        pending.current = null;
      }
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    loadComercios();
    return () => {
      mounted.current = false;
      revision.current += 1;
      pending.current?.abort();
    };
  }, [loadComercios]);
  const comercios = useMemo(() => filterCommerces(rows, filtros), [rows, filtros]);
  const stats = useMemo(() => commerceStats(comercios), [comercios]);
  const tiposComercio = useMemo(
    () => [...new Set(rows.map((row) => row.tipoComercio).filter(Boolean))].sort(),
    [rows]
  );
  const setFiltro = useCallback(
    (key, value) => setFiltros((prev) => ({ ...prev, [key]: value })),
    []
  );
  const clearFilters = useCallback(() => setFiltros(initialFilters), []);
  return {
    comercios,
    todosComercios: rows,
    loading,
    error,
    lastUpdated,
    filtros,
    stats,
    tiposComercio,
    loadComercios,
    refreshData: loadComercios,
    setFiltro,
    clearFilters,
  };
};
